import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { HeartHandshake, HandCoins, Users2, Loader2 } from "lucide-react";

const tabs = [
  { id: "volunteer", label: "Volunteer", icon: HeartHandshake },
  { id: "member", label: "Membership", icon: Users2 },
  { id: "donate", label: "Donate", icon: HandCoins },
];

const inputCls = "w-full border-0 border-b border-[var(--njas-line-strong)] bg-transparent py-3 text-njas-text focus:outline-none focus:border-njas-sage transition placeholder:text-njas-muted/60";

export default function GetInvolved() {
  const [tab, setTab] = useState("volunteer");
  return (
    <div data-testid="get-involved-page" className="fade-in">
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-16 lg:pt-24 pb-12">
        <div className="njas-eyebrow">Get Involved</div>
        <div className="njas-script text-5xl text-njas-sage-deep mt-6">Three ways to</div>
        <h1 className="njas-display text-6xl sm:text-8xl lg:text-9xl text-njas-navy mt-2 leading-[0.88]">
          STAND&nbsp;WITH<br/>US.
        </h1>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pb-24">
        <div className="flex flex-wrap border-t border-b border-[var(--njas-sage)]/50 mb-12">
          {tabs.map(({ id, label, icon: Icon }, i) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`involve-tab-${id}`}
              className={`flex-1 min-w-[140px] njas-condensed tracking-[0.22em] font-semibold text-[13px] inline-flex items-center justify-center gap-3 px-6 py-6 transition-colors ${
                tab === id ? "bg-njas-navy text-njas-bg" : "text-njas-navy hover:bg-[var(--njas-bg-alt)]"
              } ${i > 0 ? "border-l border-[var(--njas-sage)]/50" : ""}`}
            >
              <Icon className="w-4 h-4" /> 0{i + 1} · {label}
            </button>
          ))}
        </div>

        {tab === "volunteer" && <VolunteerForm />}
        {tab === "member" && <MemberForm />}
        {tab === "donate" && <DonatePanel />}
      </section>
    </div>
  );
}

function Field({ label, children, required, testid }) {
  return (
    <label className="block" data-testid={testid ? `${testid}-wrapper` : undefined}>
      <span className="njas-eyebrow text-njas-navy block">{label} {required && <span className="text-njas-sage">*</span>}</span>
      {children}
    </label>
  );
}

function VolunteerForm() {
  const [f, set] = useState({ full_name: "", email: "", phone: "", location: "", skills: "", availability: "", lived_experience: false, motivation: "" });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => set({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/volunteers", f);
      toast.success("Thank you. A coordinator will be in touch.");
      set({ full_name: "", email: "", phone: "", location: "", skills: "", availability: "", lived_experience: false, motivation: "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="grid lg:grid-cols-12 gap-16" data-testid="volunteer-form">
      <div className="lg:col-span-5">
        <h2 className="njas-display text-5xl sm:text-6xl text-njas-navy leading-[0.9]">WALK WITH US.</h2>
        <p className="mt-6 text-njas-muted leading-relaxed">
          Peer workers with lived experience, mentors, community connectors and social work students. We'd love to hear from you.
        </p>
        <div className="mt-8 pl-6 border-l-2 border-njas-sage">
          <div className="njas-eyebrow">Lived experience valued</div>
          <p className="mt-3 text-sm text-njas-text leading-relaxed">
            Some of our peer workers first connected with us through our support services and now choose to give back by supporting others.
          </p>
        </div>
      </div>

      <div className="lg:col-span-7 space-y-8">
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
          <Field label="Full name" required><input required value={f.full_name} onChange={upd("full_name")} className={inputCls} data-testid="volunteer-name" /></Field>
          <Field label="Email" required><input type="email" required value={f.email} onChange={upd("email")} className={inputCls} data-testid="volunteer-email" /></Field>
          <Field label="Phone"><input value={f.phone} onChange={upd("phone")} className={inputCls} data-testid="volunteer-phone" /></Field>
          <Field label="Suburb / State"><input value={f.location} onChange={upd("location")} className={inputCls} data-testid="volunteer-location" /></Field>
        </div>
        <Field label="Skills or background"><textarea rows={3} value={f.skills} onChange={upd("skills")} className={inputCls} data-testid="volunteer-skills" /></Field>
        <Field label="Availability"><input placeholder="Weekday evenings, 4 hrs/week" value={f.availability} onChange={upd("availability")} className={inputCls} data-testid="volunteer-availability" /></Field>
        <label className="flex items-start gap-3 text-sm text-njas-text">
          <input type="checkbox" checked={f.lived_experience} onChange={upd("lived_experience")} className="mt-1 accent-njas-navy" data-testid="volunteer-lived" />
          I have lived experience of the justice system or supporting someone who has.
        </label>
        <Field label="Why do you want to volunteer with NJAS?" required>
          <textarea rows={4} required value={f.motivation} onChange={upd("motivation")} className={inputCls} data-testid="volunteer-motivation" />
        </Field>
        <button type="submit" disabled={busy} data-testid="volunteer-submit" className="njas-condensed tracking-[0.22em] font-semibold text-[13px] inline-flex items-center gap-3 bg-njas-navy text-njas-bg px-8 py-4 hover:bg-njas-navy-hover disabled:opacity-60">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Register Interest
        </button>
      </div>
    </form>
  );
}

function MemberForm() {
  const [f, set] = useState({ full_name: "", email: "", phone: "", address: "", membership_type: "individual", reason_for_joining: "", agrees_to_guarantee: false });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => set({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.agrees_to_guarantee) return toast.error("Please agree to the members' guarantee");
    setBusy(true);
    try {
      await api.post("/public/members", f);
      toast.success("Thank you for supporting NJAS.");
      set({ full_name: "", email: "", phone: "", address: "", membership_type: "individual", reason_for_joining: "", agrees_to_guarantee: false });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="grid lg:grid-cols-12 gap-16" data-testid="member-form">
      <div className="lg:col-span-5">
        <h2 className="njas-display text-5xl sm:text-6xl text-njas-navy leading-[0.9]">BECOME A MEMBER.</h2>
        <p className="mt-6 text-njas-muted leading-relaxed">
          As a company limited by guarantee, our members are the legal foundation of NJAS. Members agree to contribute a nominal $10 guarantee if the company is ever wound up.
        </p>
      </div>
      <div className="lg:col-span-7 space-y-8">
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
          <Field label="Full name" required><input required value={f.full_name} onChange={upd("full_name")} className={inputCls} data-testid="member-name" /></Field>
          <Field label="Email" required><input type="email" required value={f.email} onChange={upd("email")} className={inputCls} data-testid="member-email" /></Field>
          <Field label="Phone"><input value={f.phone} onChange={upd("phone")} className={inputCls} data-testid="member-phone" /></Field>
          <Field label="Membership type">
            <select value={f.membership_type} onChange={upd("membership_type")} data-testid="member-type" className={inputCls}>
              <option value="individual">Individual</option>
              <option value="organisation">Organisation</option>
            </select>
          </Field>
        </div>
        <Field label="Address"><input value={f.address} onChange={upd("address")} className={inputCls} data-testid="member-address" /></Field>
        <Field label="Why do you want to become a member?"><textarea rows={3} value={f.reason_for_joining} onChange={upd("reason_for_joining")} className={inputCls} data-testid="member-reason" /></Field>
        <label className="flex items-start gap-3 text-sm text-njas-text">
          <input type="checkbox" checked={f.agrees_to_guarantee} onChange={upd("agrees_to_guarantee")} className="mt-1 accent-njas-navy" data-testid="member-agree" />
          I agree that if NJAS is ever wound up, I will contribute up to $10 to the debts of the company (standard members' guarantee).
        </label>
        <button type="submit" disabled={busy} data-testid="member-submit" className="njas-condensed tracking-[0.22em] font-semibold text-[13px] inline-flex items-center gap-3 bg-njas-navy text-njas-bg px-8 py-4 hover:bg-njas-navy-hover disabled:opacity-60">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Apply for Membership
        </button>
      </div>
    </form>
  );
}

function DonatePanel() {
  return (
    <div id="donate" className="space-y-16" data-testid="donate-panel">
      <div className="grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <h2 className="njas-display text-5xl sm:text-6xl text-njas-navy leading-[0.9]">FUND THE WORK.</h2>
          <p className="mt-6 text-njas-muted leading-relaxed">
            Every dollar donated goes directly to service delivery. Practical support, prison visits,
            reintegration costs, food, transport, family support and peer worker payments.
          </p>
        </div>
        <div className="lg:col-span-7 border-l border-[var(--njas-sage)]/50 pl-10">
          <div className="njas-eyebrow text-njas-sage-deep">Online giving arriving soon</div>
          <h3 className="njas-display text-4xl text-njas-navy mt-3 leading-tight">SECURE DONATIONS SHORTLY.</h3>
          <p className="mt-6 text-njas-muted text-sm leading-relaxed">
            We are finalising our ACNC charity registration and bank setup. Once complete, one off and
            recurring giving will be enabled on this page. In the meantime, contact us and we will help
            you give.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button disabled data-testid="donate-btn" className="njas-condensed tracking-[0.22em] font-semibold text-[13px] px-6 py-3 bg-njas-navy/40 text-njas-bg cursor-not-allowed">
              Donate Securely (soon)
            </button>
            <a href="/contact" data-testid="donate-contact-link" className="njas-condensed tracking-[0.22em] font-semibold text-[13px] px-6 py-3 border border-njas-navy text-njas-navy hover:bg-njas-navy hover:text-njas-bg transition-colors">
              Talk to us about giving
            </a>
          </div>
        </div>
      </div>

      {/* Compliance strip — required for online donation intake in Australia */}
      <div className="border-t border-[var(--njas-sage)]/50 pt-16" data-testid="donate-compliance">
        <div className="njas-eyebrow">Donor Information &amp; Compliance</div>
        <h3 className="njas-display text-4xl text-njas-navy mt-3 leading-tight">WHAT YOU NEED TO KNOW BEFORE YOU GIVE.</h3>

        <div className="mt-10 grid md:grid-cols-2 gap-x-12 gap-y-10">
          <ComplianceBlock n="01" title="Tax Deductibility Status" testid="donate-taxstatus">
            <p>
              <strong>Donations to Not Just A Statistic Ltd are not currently tax deductible.</strong>{" "}
              We do not yet hold Deductible Gift Recipient (DGR) status. This page will be updated as soon
              as our DGR endorsement is granted by the Australian Taxation Office.
            </p>
          </ComplianceBlock>

          <ComplianceBlock n="02" title="Fundraising Authority" testid="donate-fundraising">
            <p>
              We are in the process of applying for a NSW Charitable Fundraising Authority. Our
              authority number will be published here once issued.
            </p>
            <p className="text-xs text-njas-muted">
              NSW Charitable Fundraising Authority No.: <span className="text-njas-navy font-medium">TBA</span>
            </p>
          </ComplianceBlock>

          <ComplianceBlock n="03" title="Receipt Notice" testid="donate-receipt">
            <p>
              Once online giving is enabled, an automated receipt containing our ABN will be emailed
              to you immediately upon successful payment. Please retain this for your records.
            </p>
          </ComplianceBlock>

          <ComplianceBlock n="04" title="Payment Security" testid="donate-security">
            <p>
              Once activated, all payments will be processed by a PCI-DSS compliant third party payment
              gateway (Stripe). Your card details are encrypted end to end and are never captured,
              stored or viewed by NJAS.
            </p>
            <div className="flex gap-3 pt-2">
              <span className="njas-tag">SSL Encrypted</span>
              <span className="njas-tag">PCI-DSS</span>
            </div>
          </ComplianceBlock>

          <ComplianceBlock n="05" title="Refund Policy" testid="donate-refund">
            <p>
              We accept requests to refund donations made in error (for example a duplicate charge or
              incorrect amount) within 30 days of the transaction. Contact us at{" "}
              <a className="njas-link text-njas-navy" href="mailto:info@njas.org.au">info@njas.org.au</a>{" "}
              with the transaction reference to request a refund.
            </p>
          </ComplianceBlock>

          <ComplianceBlock n="06" title="Privacy" testid="donate-privacy">
            <p>
              All personal and financial data collected during a donation is handled in accordance with
              our <a href="/privacy" className="njas-link text-njas-navy">Privacy Policy</a>, the
              Australian Privacy Principles under the Privacy Act 1988 (Cth) and PCI-DSS.
            </p>
          </ComplianceBlock>
        </div>

        <div className="mt-12 text-xs text-njas-muted max-w-3xl leading-relaxed">
          Registered structure: Public Company Limited by Guarantee. ABN 65 699 934 515.
          ACNC charity registration pending. All donations are used solely to advance the objects of the
          company. All donations are used solely to advance the objects of the company.
        </div>
      </div>
    </div>
  );
}

function PartnerCell({ num, testid, logoSrc, alt, logoScale = 1, tag }) {
  return (
    <div
      data-testid={testid}
      className="group relative flex items-center justify-center px-8 py-14 min-h-[220px] hover:bg-[var(--njas-bg-alt)]/50 transition-colors"
    >
      <img
        src={logoSrc}
        alt={alt}
        className="max-h-[130px] max-w-[80%] object-contain"
        style={{ transform: `scale(${logoScale})` }}
      />
    </div>
  );
}

function ComplianceBlock({ n, title, children, testid }) {
  return (
    <div className="grid grid-cols-12 gap-4" data-testid={testid}>
      <div className="col-span-2 njas-display text-3xl text-njas-sage">{n}</div>
      <div className="col-span-10">
        <div className="njas-condensed tracking-[0.14em] font-semibold text-sm text-njas-navy">{title}</div>
        <div className="mt-3 space-y-2 text-sm text-njas-text leading-relaxed">{children}</div>
      </div>
    </div>
  );
}
